import parseDate from 'date-fns/parse';

import { programs, client, translations } from '@biw/contests-core';
import { clientFetch } from '@biw/client-fetch';

import dataUri from 'app/utils/data-uri';

import { setUserData, setLanguageOverrideFlag } from 'dux/user';
import { getUserAuthenticated } from 'dux/user/selectors';
import { addError, dismissErrorsByType, Errors } from 'dux/errors';


/******************
 * Initial state
 */
const initialState = {
    passwordChangeSuccess: false,
    accountHistory: {
        accountHistoryList: [],
        displayRows: []
    }
};
const defaultPageSize = 50;

/******************
 * Non-async action types
 */
const USER_LOCALE = 'userLocale';
const USER_AVATAR = 'userAvatar';
const USER_PASSWORD = 'userPassword';
const ACCOUNT_HISTORY = 'accountHistory';

const PASSWORD_CHANGE_SUCCESS = 'PASSWORD_CHANGE_SUCCESS';
const PROFILE_HIDE_PASSWORD_CHANGE_SUCCESS_MESSAGE = 'PROFILE_HIDE_PASSWORD_CHANGE_SUCCESS_MESSAGE';
const DISMISS_SUCCESS = 'DISMISS_SUCCESS';

const SET_ACCOUNT_HISTORY = 'SET_ACCOUNT_HISTORY';
const SORT_ACCOUNT_HISTORY = 'SORT_ACCOUNT_HISTORY';
const SHOW_MORE_ACCOUNT_HISTORY = 'SHOW_MORE_ACCOUNT_HISTORY';


/******************
 * Reducer
 */
export default function profile( state = initialState, action ) {
    switch ( action.type ) {

        case PASSWORD_CHANGE_SUCCESS:
            return {
                ...state,
                passwordChangeSuccess: true
            };

        case PROFILE_HIDE_PASSWORD_CHANGE_SUCCESS_MESSAGE:
            return {
                ...state,
                passwordChangeSuccess: false
            };

        case DISMISS_SUCCESS:
            return {
                ...state,
                passwordChangeSuccess: false
            };

        case SET_ACCOUNT_HISTORY:
            return {
                ...state,
                accountHistory: action.accountHistory
            };

        case SORT_ACCOUNT_HISTORY:
            const {
                sortedOn,
                sortedBy,
                accountHistoryList,
                rowCount
            } = state.accountHistory;
            let sortBy;

            if ( sortedOn === action.sortOn ) {
                sortBy = ( sortedBy === 'asc' ) ? 'desc' : 'asc';
            } else {
                sortBy = 'asc';
            }

            const newDisplayRows = sortDisplayRows( accountHistoryList, action.sortOn, sortBy );
            if ( rowCount < accountHistoryList.length ) {
                newDisplayRows.splice( state.accountHistory.rowCount );
            }

            return {
                ...state,
                accountHistory: {
                    ...state.accountHistory,
                    sortedOn: action.sortOn,
                    sortedBy: sortBy,
                    displayRows: newDisplayRows
                }
            };

        case SHOW_MORE_ACCOUNT_HISTORY:
            const newRowCount = state.accountHistory.rowCount + state.accountHistory.pageSize;
            const moreDisplayRows = [ ...state.accountHistory.accountHistoryList ];
            moreDisplayRows.splice( newRowCount );
            return {
                ...state,
                accountHistory: {
                    ...state.accountHistory,
                    rowCount: newRowCount,
                    displayRows: moreDisplayRows
                }
            };

        default:
            return state;
    }
}


/******************
 * Action creators
 */
export function setUserLocale( localeCode ) {
    return async ( dispatch, getState ) => {
        try {
            await dispatch( client.actions.getLocaleBundle( localeCode ) );

            dispatch( client.actions.setUserLocale( localeCode ) );
            const isLoggedIn = getUserAuthenticated( getState() );
            dispatch( setLanguageOverrideFlag( !isLoggedIn ) );
            const locale = translations.selectors.getUserLocale( getState() );
            document.getElementsByTagName( 'html' )[ 0 ].setAttribute( 'lang', locale.intlCode );
            // Only set locale on the server if the user is logged in
            if ( isLoggedIn ) {
                const user = await clientFetch( {
                    url: '/api/v1.0/user/locale',
                    method: 'post',
                    body: { locale: localeCode },
                    type: USER_LOCALE,
                    catchError: true
                } );
                dispatch( setUserData( user ) );
                dispatch( programs.actions.getProgramViews() );
                const localeIntl = translations.selectors.getUserLocale( getState() );
                document.getElementsByTagName( 'html' )[ 0 ].setAttribute( 'lang', localeIntl.intlCode );
            }
        } catch ( error ) {
            dispatch( addError( error, Errors.ERROR_PROFILE_SETUSERLOCALE ) );
        }
    };
}


export function uploadUserAvatar( avatarDataUri ) {
    return ( dispatch, getState ) => {

        const pictureBlob = dataUri.toBlob( avatarDataUri );
        const formData = new FormData();
        formData.append( 'profile-pic', pictureBlob, 'avatar.jpg' );
        formData.append( 'userId', getState().user.userId );
        formData.append( 'existingAvatarUrl', getState().user.avatarUrl );

        return clientFetch( {
                url: '/api/v1.0/user/avatar',
                method: 'post',
                body: formData,
                bodyIsFormData: true,
                type: USER_AVATAR,
                catchError: true
            } )
            .then( ( result ) => {
                if ( result.errorCode ) {
                    dispatch( addError( result, Errors.ERROR_PROFILE_UPLOADUSERAVATAR ) );
                } else {
                    dispatch( setUserData( { avatarUrl: result.url } ) );
                }
            } )
            .catch( ( error ) => {
                console.log( 'ERROR uploading the avatar image', error );
                dispatch( addError( error, Errors.ERROR_PROFILE_UPLOADUSERAVATAR ) );
            } );
    };
}

export function changePassword( oldPassword, newPassword ) {

    return ( dispatch, getState ) => {
        dispatch( dismissErrorsByType( Errors.ERROR_PROFILE_CHANGEPASSWORD ) );
        dispatch( hidePasswordChangeSuccessMessage() );

        return clientFetch( {
                url: '/api/v1.0/user/password',
                method: 'put',
                body: { oldPassword, newPassword },
                type: USER_PASSWORD,
                catchError: true
            } )
            .then( ( result ) => {
                if ( result.errorCode ) {
                    dispatch( addError( result, Errors.ERROR_PROFILE_CHANGEPASSWORD ) );
                } else {
                    dispatch( notifyChangeSuccess() );
                }
            } );
    };
}

export function fetchAccountHistory() {

    return ( dispatch, getState ) => {

        return clientFetch( {
                url: '/api/v1.0/user/account/statement/all',
                method: 'GET',
                type: ACCOUNT_HISTORY,
                catchError: true
            } )
            .then( ( result ) => {
                const accountHistory = result;
                accountHistory.displayRows = [ ...accountHistory.accountHistoryList ];
                accountHistory.pageSize = defaultPageSize;
                accountHistory.rowCount = defaultPageSize;

                accountHistory.displayRows.splice( defaultPageSize );

                dispatch( {
                    type: SET_ACCOUNT_HISTORY,
                    accountHistory: result
                } );
            } )
            .catch( ( error ) => {
                console.log( 'ERROR fetching account history', error );
                dispatch( addError( error, Errors.ERROR_PROFILE_ACCOUNT_HISTORY ) );
            } );
    };
}


export function notifyChangeSuccess() {
    return {
        type: PASSWORD_CHANGE_SUCCESS
    };
}

function hidePasswordChangeSuccessMessage() {
    return {
        type: PROFILE_HIDE_PASSWORD_CHANGE_SUCCESS_MESSAGE
    };
}


export function dismissSuccess() {
    return {
        type: DISMISS_SUCCESS
    };
}


export function sortAccountHistory( sortOn ) {
    return {
        type: SORT_ACCOUNT_HISTORY,
        sortOn
    };
}


export function showMoreAccountHistory() {
    return {
        type: SHOW_MORE_ACCOUNT_HISTORY
    };
}


function sortDisplayRows( rows, sortOn, sortBy ) {
    const sortSwitch = function( x, y ) {
        switch ( sortBy ) {
            case 'asc':
                return x < y ? -1 : x > y ? 1 : 0;
            case 'desc':
                return x < y ? 1 : x > y ? -1 : 0;
            default:
                console.error( 'Sort by value does not match "asc" or "desc"' );
                return;
        }
    };

    switch ( sortOn ) {
        case 'date':
            rows.sort( ( a, b ) => {
                const x = parseDate( a.date );
                const y = parseDate( b.date );
                return ( sortBy === 'asc' ) ? x - y : y - x;
            } );
            break;

        case 'description':
            rows.sort( ( a, b ) => {
                return sortSwitch( a.description.toLowerCase(), b.description.toLowerCase() );
            } );
            break;

        case 'amount':
            rows.sort( ( a, b ) => {
                return sortSwitch( parseInt( a.amountOrAward, 10 ), parseInt( b.amountOrAward, 10 ) );
            } );
            break;
        default:
            console.error( 'Account history table sort did not recieve a proper sortOn value. Instead received:', sortOn );
    }

    return [ ...rows ];
}
